import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { ProductDetail, ProductVariant } from '@/api/models';
import {
    computeLineMarkup,
    DEFAULT_UNIT,
    DEFAULT_VAT,
    UNIT_OPTIONS,
    VAT_OPTIONS,
    type ExistingLinePick,
    type ImportLineDraft,
} from '@/pages/purchases/manualImport/manualImportMeta';
import {
    TEMPLATE_COLUMNS,
    type TemplateColumnKey,
} from '../purchaseHeader/purchaseTemplateMeta';

// Strict 'YYYY-MM-DD' validation needs the plugin; extend() is idempotent.
dayjs.extend(customParseFormat);

/**
 * Pure core of the Excel-import analyzer: header mapping, cell coercion,
 * name → id resolution and row → line conversion. No exceljs, no React, no
 * i18next — translators, catalog maps and barcode matches are injected, so
 * every function here unit-tests directly. The exceljs adapter lives in
 * {@link ./excelParse}; the fetch side in {@link ./barcodeMatcher}.
 */

/** One data row lifted from the Products sheet, cells normalized to strings. */
export type RawRow = {
    /** 1-based Excel row number (header is row 1). */
    rowNumber: number;
    cells: Partial<Record<TemplateColumnKey, string>>;
};

export type RowIssueKind =
    | 'missingBarcode'
    | 'missingSku'
    | 'missingName'
    | 'invalidQuantity'
    | 'invalidPurchasePrice'
    | 'invalidSalePrice'
    | 'invalidWholesalePrice'
    | 'invalidMinimumQuantity'
    | 'invalidUnit'
    | 'invalidVat'
    | 'unknownCategory'
    | 'unknownBrand'
    | 'invalidExpiryDate'
    | 'invalidSaleDueDate'
    | 'unknownSupplier'
    | 'supplierMismatch'
    | 'lookupFailed';

export type RowIssue = {
    kind: RowIssueKind;
    column: TemplateColumnKey;
    /** The offending raw cell value (may be ''). */
    value: string;
};

/** A line shaped for the manual-import form, key assigned by the consumer. */
export type PreparedLine = Omit<ImportLineDraft, 'key'>;

export type ConvertedRow =
    | { status: 'clean'; rowNumber: number; line: PreparedLine }
    | {
          status: 'doubtful';
          rowNumber: number;
          raw: RawRow['cells'];
          issues: RowIssue[];
          /** Best-effort line (invalid cells fall back to defaults/'') used by Promote. */
          suggestion: PreparedLine;
      };

export type DoubtfulRow = {
    id: string;
    rowNumber: number;
    raw: RawRow['cells'];
    issues: RowIssue[];
    suggestion: PreparedLine;
};

export type AnalysisResult = {
    fileName: string;
    /** Elected majority supplier; null when no supplier name resolved. */
    supplierId: string | null;
    /** Clean rows, keyed `file-row-${rowNumber}` (never collides with the
     *  form's internal `row-N` counter). */
    lines: ImportLineDraft[];
    doubtful: DoubtfulRow[];
};

export type ParseErrorKind =
    'notTemplate' | 'emptyFile' | 'tooManyRows' | 'readFailed';

/** Thrown by the parser/analyzer; `kind` selects the localized error copy. */
export class ExcelImportError extends Error {
    constructor(public readonly kind: ParseErrorKind) {
        super(`excel import failed: ${kind}`);
        this.name = 'ExcelImportError';
    }
}

/* -------------------------------------------------------------------------- */
/* Header mapping                                                             */
/* -------------------------------------------------------------------------- */

const normalizeLabel = (value: string): string =>
    value.trim().toLocaleLowerCase();

/**
 * Build the header-label → column-key map from one translator per supported
 * locale (`i18n.getFixedT(lng, 'pages')`), so a ka-generated file imports on
 * an en UI and vice versa. Canonical keys are also accepted as labels.
 */
export const buildLabelToKey = (
    translators: ReadonlyArray<(key: string) => string>,
): Map<string, TemplateColumnKey> => {
    const map = new Map<string, TemplateColumnKey>();
    for (const column of TEMPLATE_COLUMNS) {
        map.set(normalizeLabel(column.key), column.key);
        for (const t of translators) {
            map.set(normalizeLabel(t(column.labelKey)), column.key);
        }
    }
    return map;
};

/** Columns without which a sheet can't be treated as the purchase template. */
const REQUIRED_HEADER_KEYS: readonly TemplateColumnKey[] = [
    'barcode',
    'name',
    'quantity',
];

/**
 * Map header-row cells to column keys (tolerant to column reordering and
 * removed optional columns; unknown headers are ignored). Returns `null` when
 * the required columns are absent — i.e. this is not our template.
 */
export const buildHeaderIndex = (
    headerCells: ReadonlyArray<string>,
    labelToKey: Map<string, TemplateColumnKey>,
): Map<number, TemplateColumnKey> | null => {
    const index = new Map<number, TemplateColumnKey>();
    const seen = new Set<TemplateColumnKey>();
    headerCells.forEach((cell, cellIndex) => {
        const key = labelToKey.get(normalizeLabel(cell));
        // First occurrence wins if a label somehow repeats.
        if (key && !seen.has(key)) {
            seen.add(key);
            index.set(cellIndex, key);
        }
    });
    const hasRequired = REQUIRED_HEADER_KEYS.every((key) => seen.has(key));
    return hasRequired ? index : null;
};

/* -------------------------------------------------------------------------- */
/* Value coercion                                                             */
/* -------------------------------------------------------------------------- */

const parseNumber = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
};

const isValidDate = (value: string): boolean =>
    dayjs(value, 'YYYY-MM-DD', true).isValid();

const findEnum = <T extends string>(
    options: readonly T[],
    value: string,
): T | null =>
    options.find(
        (option) => normalizeLabel(option) === normalizeLabel(value),
    ) ?? null;

/* -------------------------------------------------------------------------- */
/* Row conversion                                                             */
/* -------------------------------------------------------------------------- */

export type BarcodeMatch = ExistingLinePick | 'failed';

/** Everything `convertRow` needs, resolved up-front so it stays pure. */
export type ConvertContext = {
    /** Elected majority supplier (see {@link electSupplier}). */
    supplierId: string | null;
    /** Lowercased entity name → id. */
    suppliersByName: Map<string, string>;
    categoriesByName: Map<string, string>;
    brandsByName: Map<string, string>;
    /** Exact-barcode catalog matches; absent key = no match (new product). */
    barcodeMatch: Map<string, BarcodeMatch>;
};

/** Lowercased-name → id map from `{id, name}` lists. First name wins. */
export const byName = (
    items: ReadonlyArray<{ id: string; name: string }>,
): Map<string, string> => {
    const map = new Map<string, string>();
    for (const item of items) {
        const key = normalizeLabel(item.name);
        if (!map.has(key)) map.set(key, item.id);
    }
    return map;
};

/**
 * Elect the form-level supplier: the most frequent resolvable supplier name
 * across rows (ties → first encountered). Null when nothing resolves.
 */
export const electSupplier = (
    rows: ReadonlyArray<RawRow>,
    suppliersByName: Map<string, string>,
): string | null => {
    const counts = new Map<string, number>();
    const order: string[] = [];
    for (const row of rows) {
        const cell = (row.cells.supplier ?? '').trim();
        if (!cell) continue;
        const id = suppliersByName.get(normalizeLabel(cell));
        if (!id) continue;
        if (!counts.has(id)) order.push(id);
        counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    let winner: string | null = null;
    let best = 0;
    for (const id of order) {
        const count = counts.get(id) ?? 0;
        if (count > best) {
            best = count;
            winner = id;
        }
    }
    return winner;
};

/** Same variant → pick mapping the manual-import ProductPicker performs. */
export const pickFromVariant = (
    detail: ProductDetail,
    variant: ProductVariant,
): ExistingLinePick => {
    const optionValues: Record<string, string> = {};
    for (const option of variant.options) {
        optionValues[option.attributeTypeId] = option.attributeValueId;
    }
    return {
        variantId: variant.id,
        productId: detail.id,
        name: detail.name,
        sku: detail.sku,
        barcode: variant.barcode,
        categoryId: detail.category?.id ?? null,
        brandId: detail.brand?.id ?? null,
        unit: detail.unit,
        vatType: detail.vatType,
        optionValues,
        salePrice: variant.salePrice,
        wholesalePrice: variant.wholesalePrice,
        minimumQuantity: variant.minimumQuantity,
    };
};

/** Positive prefill → raw string, '' when ≤ 0 / null (mirrors makeExistingLine). */
const prefillNumber = (value: number | null): string =>
    value !== null && value > 0 ? String(value) : '';

/**
 * Convert one parsed row into a form line (clean) or a doubtful entry. Every
 * doubtful row still carries a best-effort `suggestion` so Promote can append
 * it for in-grid fixing.
 */
export const convertRow = (raw: RawRow, ctx: ConvertContext): ConvertedRow => {
    const cell = (key: TemplateColumnKey): string =>
        (raw.cells[key] ?? '').trim();
    const issues: RowIssue[] = [];
    const issue = (kind: RowIssueKind, column: TemplateColumnKey) =>
        issues.push({ kind, column, value: cell(column) });

    // Supplier: empty inherits the form-level supplier; a named one must
    // resolve to the elected supplier.
    const supplierCell = cell('supplier');
    if (supplierCell) {
        const supplierId = ctx.suppliersByName.get(
            normalizeLabel(supplierCell),
        );
        if (!supplierId) issue('unknownSupplier', 'supplier');
        else if (supplierId !== ctx.supplierId)
            issue('supplierMismatch', 'supplier');
    }

    // Shared numerics (validated identically for both modes).
    const quantity = parseNumber(cell('quantity'));
    if (quantity === null || quantity <= 0)
        issue('invalidQuantity', 'quantity');
    const purchasePrice = parseNumber(cell('purchasePrice'));
    if (purchasePrice === null || purchasePrice < 0)
        issue('invalidPurchasePrice', 'purchasePrice');
    const wholesale = cell('wholesalePrice');
    const wholesaleParsed = parseNumber(wholesale);
    if (wholesale && (wholesaleParsed === null || wholesaleParsed < 0))
        issue('invalidWholesalePrice', 'wholesalePrice');
    const minimum = cell('minimumQuantity');
    const minimumParsed = parseNumber(minimum);
    if (minimum && (minimumParsed === null || minimumParsed < 0))
        issue('invalidMinimumQuantity', 'minimumQuantity');

    // Optional dates ('' allowed; parser already normalizes Date cells).
    const expiry = cell('expiryDate');
    if (expiry && !isValidDate(expiry))
        issue('invalidExpiryDate', 'expiryDate');
    const saleDue = cell('saleDueDate');
    if (saleDue && !isValidDate(saleDue))
        issue('invalidSaleDueDate', 'saleDueDate');

    const sanitizedNumerics = {
        quantity: quantity !== null && quantity > 0 ? cell('quantity') : '',
        purchasePrice:
            purchasePrice !== null && purchasePrice >= 0
                ? cell('purchasePrice')
                : '',
        wholesalePrice:
            wholesaleParsed !== null && wholesaleParsed >= 0 ? wholesale : '',
        minimumQuantity:
            minimumParsed !== null && minimumParsed >= 0 ? minimum : '',
        markup: '', // derived from cost + sale price once the line is built
        batchNumber: '',
        expiryDate: expiry && isValidDate(expiry) ? expiry : '',
        saleDueDate: saleDue && isValidDate(saleDue) ? saleDue : '',
    };

    const barcode = cell('barcode');
    const match = barcode ? ctx.barcodeMatch.get(barcode) : undefined;

    let line: PreparedLine;
    if (match && match !== 'failed') {
        // Restock of an existing variant — catalog identity wins over the
        // file's identity cells; file prices override the variant prefill.
        const salePriceCell = cell('salePrice');
        const salePriceParsed = parseNumber(salePriceCell);
        if (salePriceCell && (salePriceParsed === null || salePriceParsed < 0))
            issue('invalidSalePrice', 'salePrice');
        line = {
            mode: 'existing',
            variantId: match.variantId,
            productId: match.productId,
            name: match.name,
            sku: match.sku ?? '',
            barcode: match.barcode,
            categoryId: match.categoryId,
            brandId: match.brandId,
            unit: match.unit,
            vatType: match.vatType,
            optionValues: match.optionValues,
            ...sanitizedNumerics,
            salePrice:
                salePriceParsed !== null && salePriceParsed >= 0
                    ? salePriceCell
                    : prefillNumber(match.salePrice),
            wholesalePrice:
                sanitizedNumerics.wholesalePrice ||
                prefillNumber(match.wholesalePrice),
            minimumQuantity:
                sanitizedNumerics.minimumQuantity ||
                prefillNumber(match.minimumQuantity),
        };
    } else {
        if (match === 'failed') issue('lookupFailed', 'barcode');

        // New product defined by the row.
        if (!barcode) issue('missingBarcode', 'barcode');
        if (!cell('sku')) issue('missingSku', 'sku');
        if (!cell('name')) issue('missingName', 'name');

        const categoryCell = cell('category');
        const categoryId = categoryCell
            ? (ctx.categoriesByName.get(normalizeLabel(categoryCell)) ?? null)
            : null;
        if (categoryCell && !categoryId) issue('unknownCategory', 'category');

        const brandCell = cell('brand');
        const brandId = brandCell
            ? (ctx.brandsByName.get(normalizeLabel(brandCell)) ?? null)
            : null;
        if (brandCell && !brandId) issue('unknownBrand', 'brand');

        // Empty unit/VAT silently take the form defaults (same as manual entry).
        const unitCell = cell('unit');
        const unit = unitCell ? findEnum(UNIT_OPTIONS, unitCell) : DEFAULT_UNIT;
        if (!unit) issue('invalidUnit', 'unit');
        const vatCell = cell('vat');
        const vatType = vatCell ? findEnum(VAT_OPTIONS, vatCell) : DEFAULT_VAT;
        if (!vatType) issue('invalidVat', 'vat');

        const salePriceCell = cell('salePrice');
        const salePriceParsed = parseNumber(salePriceCell);
        if (salePriceParsed === null || salePriceParsed < 0)
            issue('invalidSalePrice', 'salePrice');

        line = {
            mode: 'new',
            variantId: null,
            productId: null,
            name: cell('name'),
            sku: cell('sku'),
            barcode,
            categoryId,
            brandId,
            unit: unit ?? DEFAULT_UNIT,
            vatType: vatType ?? DEFAULT_VAT,
            optionValues: {},
            ...sanitizedNumerics,
            salePrice:
                salePriceParsed !== null && salePriceParsed >= 0
                    ? salePriceCell
                    : '',
        };
    }

    line = {
        ...line,
        markup: computeLineMarkup(line.purchasePrice, line.salePrice),
    };

    if (issues.length === 0) {
        return { status: 'clean', rowNumber: raw.rowNumber, line };
    }
    return {
        status: 'doubtful',
        rowNumber: raw.rowNumber,
        raw: raw.cells,
        issues,
        suggestion: line,
    };
};

/** Convert all rows and split them into form lines + doubtful entries. */
export const buildAnalysisResult = (
    rows: ReadonlyArray<RawRow>,
    ctx: ConvertContext,
    fileName: string,
): AnalysisResult => {
    const lines: ImportLineDraft[] = [];
    const doubtful: DoubtfulRow[] = [];
    for (const raw of rows) {
        const converted = convertRow(raw, ctx);
        if (converted.status === 'clean') {
            lines.push({
                ...converted.line,
                key: `file-row-${converted.rowNumber}`,
            });
        } else {
            doubtful.push({
                id: `doubtful-${converted.rowNumber}`,
                rowNumber: converted.rowNumber,
                raw: converted.raw,
                issues: converted.issues,
                suggestion: converted.suggestion,
            });
        }
    }
    return { fileName, supplierId: ctx.supplierId, lines, doubtful };
};
